import { useGetCustomer, useResendEmailVerification, useResetCustomerPassword } from 'api/Customers'
import { useParams } from 'react-router-dom'
import { DateTime } from 'luxon'
import { EditDetailsModal } from './EditDetailsModal'
import { Badge } from 'ui/components/Badge'
import { useNotificationContext, Notification } from 'ui/components/Notification'
import { Spinner } from 'ui/components/Spinner'
import { DescriptionItem } from 'ui/components/DescriptionItem'
import { LockClosedIcon } from '@heroicons/react/solid'
import { LocaleDescriptor } from 'ui/components/LocaleDescriptor'
import { Text } from 'ui/components/Text'
import { useCurrentUserCanForCurrentBrand } from 'hooks/UseCurrentUserCan'
import { CUSTOMERS_MANAGE } from 'constants/permissions'
import { useTranslation } from 'react-i18next'

const useFields = () => {
  const { t } = useTranslation()

  return [
    {
      label: t('clients.details.name'),
      formatter: (customer) => `${customer.first_name} ${customer.last_name}`,
    },
    {
      label: t('clients.details.email'),
      formatter: (customer) => customer.email || null,
    },
    {
      label: t('clients.details.phone'),
      formatter: (customer) => customer.phone,
    },
    {
      label: t('clients.details.legal_name'),
      formatter: (customer) => customer.legal_name || null,
    },
    {
      label: t('clients.details.tax_id'),
      formatter: (customer) => customer.tax_id || null,
    },
    {
      label: t('clients.details.date_of_birth'),
      formatter: (customer) => {
        if (!customer.dob) {
          return null
        }

        const dobString = DateTime.fromJSDate(customer.dob).toLocaleString(DateTime.DATE_FULL)
        const isBirthday =
          customer.dob.getDate() === new Date().getDate() &&
          customer.dob.getMonth() === new Date().getMonth()

        return `${dobString}${isBirthday ? ' 🎂' : ''}`
      },
    },
    {
      label: t('clients.details.customer_since'),
      formatter: (customer) =>
        DateTime.fromJSDate(customer.created_at).toLocaleString(DateTime.DATE_MED),
    },
    {
      label: t('clients.details.password'),
      formatter: (customer) =>
        customer.has_password ? (
          <>
            <span>&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;</span>
            <SendPasswordResetButton clientId={customer.id} />
          </>
        ) : (
          <>
            <span className="italic text-gray-500">{t('clients.details.not_set')}</span>
            <SendSetPasswordEmailButton clientId={customer.id} />
          </>
        ),
    },
    {
      label: t('clients.details.email_verified'),
      formatter: (customer) => (
        <div className="-ml-3">
          {customer.email_verified_at && (
            <Badge variant="success" label={t('clients.details.verified')} />
          )}
          {!customer.email_verified_at && (
            <>
              <Badge variant="warning" label={t('clients.details.not_verified')} />
              <ResendEmailVerificationButton clientId={customer.id} />
            </>
          )}
        </div>
      ),
    },
    {
      label: t('clients.details.last_checked_in'),
      formatter: (customer) =>
        customer.last_check_in ? (
          <div>
            <div>
              {DateTime.fromJSDate(customer.last_check_in.checked_in_at).toLocaleString(
                DateTime.DATETIME_MED
              )}
            </div>
            {customer.last_check_in.method && (
              <div className="text-xs">
                {t('clients.details.via', { method: customer.last_check_in.method })}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-500 italic">{t('clients.details.never')}</span>
        ),
    },
    {
      label: t('clients.details.preferred_language'),
      formatter: (customer) => <LocaleDescriptor>{customer.preferred_locale}</LocaleDescriptor>,
    },
  ]
}

const SendPasswordResetButton = ({ clientId }) => {
  const { t } = useTranslation()
  const can = useCurrentUserCanForCurrentBrand()
  const userCanManageClients = can(CUSTOMERS_MANAGE)

  const { isLoading, isSuccess, mutate } = useResetCustomerPassword(clientId)
  const notification = useNotificationContext()

  const send = () =>
    mutate(null, {
      onSuccess: () => {
        notification.notify(
          <Notification
            title={t('clients.details.notification.password_reset.title')}
            description={t('clients.details.notification.password_reset.description')}
            variant="success"
            autoDismiss
          />
        )
      },
      onError: (e: any) => {
        notification.notify(
          e.status === 429 ? (
            <Notification
              title={t('clients.details.notification.password_reset.throttled.title')}
              description={t('clients.details.notification.password_reset.throttled.description')}
              variant="warning"
              autoDismiss
            />
          ) : (
            <Notification
              title={t('clients.details.notification.password_reset.error.title')}
              description={t('clients.details.notification.password_reset.error.description')}
              variant="danger"
              autoDismiss
            />
          )
        )
      },
    })

  if (isSuccess) return null

  return (
    <button
      className={`ml-1 font-medium text-xs text-blue-600 hover:text-blue-700 ${
        !userCanManageClients && 'cursor-not-allowed'
      }`}
      onClick={send}
      disabled={isLoading || !userCanManageClients}
    >
      {isLoading && (
        <div className="absolute -mt-5">
          <Spinner />
        </div>
      )}
      {!isLoading && t('clients.details.password_reset')}
    </button>
  )
}

const SendSetPasswordEmailButton = ({ clientId }) => {
  const { t } = useTranslation()
  const can = useCurrentUserCanForCurrentBrand()
  const userCanManageClients = can(CUSTOMERS_MANAGE)

  const { isLoading, isSuccess, mutate } = useResetCustomerPassword(clientId)
  const notification = useNotificationContext()
  const send = () =>
    mutate(null, {
      onSuccess: () => {
        notification.notify(
          <Notification
            title={t('clients.details.notification.setup_email.title')}
            description={t('clients.details.notification.setup_email.description')}
            variant="success"
            autoDismiss
          />
        )
      },
      onError: (e: any) => {
        notification.notify(
          e.status === 429 ? (
            <Notification
              title={t('clients.details.notification.setup_email.throttled.title')}
              description={t('clients.details.notification.setup_email.throttled.description')}
              variant="warning"
              autoDismiss
            />
          ) : (
            <Notification
              title={t('clients.details.notification.setup_email.error.title')}
              description={t('clients.details.notification.setup_email.error.description')}
              variant="danger"
              autoDismiss
            />
          )
        )
      },
    })

  if (isSuccess) return null

  return (
    <button
      className={`ml-1 font-medium text-xs text-blue-600 hover:text-blue-700 ${
        !userCanManageClients && 'cursor-not-allowed'
      } `}
      onClick={send}
      disabled={isLoading || !userCanManageClients}
    >
      {isLoading && (
        <div className="absolute -mt-5">
          <Spinner />
        </div>
      )}
      {!isLoading && t('clients.details.send_setup_email')}
    </button>
  )
}

const ResendEmailVerificationButton = ({ clientId }) => {
  const { t } = useTranslation()
  const can = useCurrentUserCanForCurrentBrand()
  const userCanManageClients = can(CUSTOMERS_MANAGE)

  const { isLoading, isSuccess, mutate } = useResendEmailVerification(clientId)
  const notification = useNotificationContext()

  const send = () =>
    mutate(null, {
      onSuccess: () => {
        notification.notify(
          <Notification
            title={t('clients.details.notification.verification.title')}
            description={t('clients.details.notification.verification.description')}
            variant="success"
            autoDismiss
          />
        )
      },
      onError: (e) => {
        notification.notify(
          e.status === 429 ? (
            <Notification
              title={t('clients.details.notification.verification.throttled.title')}
              description={t('clients.details.notification.verification.throttled.description')}
              variant="warning"
              autoDismiss
            />
          ) : (
            <Notification
              title={t('clients.details.notification.verification.error.title')}
              description={t('clients.details.notification.verification.error.description')}
              variant="danger"
              autoDismiss
            />
          )
        )
      },
    })

  if (isSuccess) return null

  return (
    <button
      className={`ml-1 font-medium text-xs text-blue-600 hover:text-blue-700 ${
        !userCanManageClients && 'cursor-not-allowed'
      } `}
      onClick={send}
      disabled={isLoading || !userCanManageClients}
    >
      {isLoading && (
        <div className="absolute -mt-5">
          <Spinner />
        </div>
      )}
      {!isLoading && t('clients.details.resend_verification')}
    </button>
  )
}

export const Details = () => {
  const { t } = useTranslation()
  const fields = useFields().filter((field) => field !== null)
  const { clientId } = useParams<{ clientId: string }>()
  const { isLoading, data: { data: client = {} } = {} } = useGetCustomer(clientId)
  return (
    <div className="col-span-1">
      <div className="mt-4 lg:mt-0">
        <div className="flex justify-between items-center border-b border-violet-200 lg:border-transparent p-3 ">
          <div className="flex space-x-1 items-center">
            {client.locked_at && <LockClosedIcon className="w-5 h-5" />}

            <Text type="title3">{t('clients.details.title')}</Text>
          </div>
          {client && client.deleted_at === null && <EditDetailsModal client={client} />}
        </div>
        <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4 p-3">
          {fields.map((field) => (
            <div key={field.label} className="sm:col-span-1">
              <DescriptionItem label={field.label}>
                {isLoading && <div className="w-16 h-4 bg-grape-200 animate-pulse" />}
                {!isLoading && client.deleted_at && (
                  <span className="bg-grape-100 rounded-lg font-medium px-2 py-1 text-xs text-gray-500">
                    {t('clients.details.deleted')}
                  </span>
                )}
                {!isLoading &&
                  client.deleted_at === null &&
                  (field.formatter(client) ?? (
                    <span className="italic text-gray-500">{t('clients.details.not_set')}</span>
                  ))}
              </DescriptionItem>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}
